// REST + WebSocket helpers. Same-origin in production; Vite proxies /api and /ws to :8080 in dev.
import type { GnssFix, LonLat } from '@fla/core';
import { authHeader, token, updateToken } from './auth';

/** fetch + bearer (when signed in) + a token refresh-and-retry on 401. In standalone mode the auth
 *  header is empty and nothing 401s, so this is a no-op wrapper. */
async function apiFetch(input: string, init: RequestInit = {}): Promise<Response> {
  const withAuth = (): RequestInit => ({
    ...init,
    headers: { ...(init.headers ?? {}), ...authHeader() },
  });
  await updateToken();
  let res = await fetch(input, withAuth());
  if (res.status === 401) {
    await updateToken();
    res = await fetch(input, withAuth());
  }
  return res;
}

export interface FieldFeature {
  type: 'Feature';
  id?: string | number;
  properties: Record<string, unknown>;
  geometry: { type: 'Polygon'; coordinates: number[][][] };
}

export interface FieldCollection {
  type: 'FeatureCollection';
  features: FieldFeature[];
  source: 'wfs' | 'cache' | 'sample';
}

export type Bbox = [number, number, number, number];

/** Server-advertised runtime config; the client fetches this first and adapts. */
export interface AppConfig {
  authRequired: boolean;
  gnssSource: string;
  allowIngest: boolean;
  connectUrl: string | null;
  oidc: { issuer: string; realm: string; clientId: string } | null;
}

export async function fetchConfig(): Promise<AppConfig | null> {
  try {
    const res = await fetch('/api/config');
    if (!res.ok) return null;
    return (await res.json()) as AppConfig;
  } catch {
    return null;
  }
}

export async function fetchFields(bbox: Bbox): Promise<FieldCollection> {
  const res = await apiFetch(`/api/fields?bbox=${bbox.join(',')}`);
  if (!res.ok) throw new Error(`fields ${res.status}`);
  return (await res.json()) as FieldCollection;
}

async function post(path: string, body: unknown): Promise<void> {
  await apiFetch(path, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
}

async function postJson<T>(path: string, body: unknown): Promise<T> {
  const res = await apiFetch(path, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`${path} ${res.status}`);
  return (await res.json()) as T;
}

export interface SavedCourse {
  id: string;
  name: string;
  field: FieldFeature;
  widthM: number;
  headingDeg: number;
  headlandM: number;
  createdAt: string;
}

export async function listCourses(): Promise<SavedCourse[]> {
  const res = await apiFetch('/api/courses');
  if (!res.ok) return [];
  return (await res.json()) as SavedCourse[];
}

export async function saveCourse(
  c: Omit<SavedCourse, 'id' | 'createdAt'> & { id?: string },
): Promise<SavedCourse> {
  const res = await apiFetch('/api/courses', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(c),
  });
  if (!res.ok) throw new Error(`save course ${res.status}`);
  return (await res.json()) as SavedCourse;
}

export const deleteCourse = (id: string) => apiFetch(`/api/courses/${id}`, { method: 'DELETE' });

// --- Field work-sessions (resume state) ---

export type SessionStatus = 'active' | 'paused' | 'done';

export interface WorkSession {
  id: string;
  userId: string;
  fieldKey: string;
  field: FieldFeature;
  activeCourseId: string | null;
  widthM: number;
  headingDeg: number;
  headlandRounds: number;
  courseMode: 'parallel' | 'contour';
  trail: LonLat[];
  lastActiveLaneIndex: number | null;
  status: SessionStatus;
  startedAt: string;
  updatedAt: string;
  finishedAt: string | null;
}

export type WorkSessionInput = Omit<
  WorkSession,
  'id' | 'userId' | 'startedAt' | 'updatedAt' | 'finishedAt'
> & { id?: string };

export async function listSessions(): Promise<WorkSession[]> {
  const res = await apiFetch('/api/sessions');
  if (!res.ok) return [];
  return (await res.json()) as WorkSession[];
}

/** The unfinished session on a field, or null if there's nothing to resume. */
export async function getActiveSession(fieldKey: string): Promise<WorkSession | null> {
  const res = await apiFetch(`/api/sessions/active?fieldKey=${encodeURIComponent(fieldKey)}`);
  if (!res.ok) return null;
  return (await res.json()) as WorkSession | null;
}

export const saveSession = (s: WorkSessionInput) => postJson<WorkSession>('/api/sessions', s);

export const finishSession = (id: string) =>
  apiFetch(`/api/sessions/${id}/finish`, { method: 'POST' });

export const deleteSession = (id: string) => apiFetch(`/api/sessions/${id}`, { method: 'DELETE' });

// --- Per-user settings + implements (cloud profile in cloud mode; local files in standalone) ---

export interface Settings {
  userId: string;
  guidanceUi: 'arrow' | 'lightbar';
  language: 'de' | 'en';
  lastFieldKey: string | null;
  updatedAt: string;
}

export async function getSettings(): Promise<Settings | null> {
  const res = await apiFetch('/api/settings');
  if (!res.ok) return null;
  return (await res.json()) as Settings;
}

export function saveSettings(
  patch: Partial<Omit<Settings, 'userId' | 'updatedAt'>>,
): Promise<unknown> {
  return apiFetch('/api/settings', {
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(patch),
  });
}

export interface Implement {
  id: string;
  userId: string;
  name: string;
  widthM: number;
  offsetM: number;
  createdAt: string;
}

export async function listImplements(): Promise<Implement[]> {
  const res = await apiFetch('/api/implements');
  if (!res.ok) return [];
  return (await res.json()) as Implement[];
}

export const saveImplement = (i: { id?: string; name: string; widthM: number; offsetM?: number }) =>
  postJson<Implement>('/api/implements', i);

export const deleteImplement = (id: string) =>
  apiFetch(`/api/implements/${id}`, { method: 'DELETE' });

/** Push one device-geolocation fix into the server's ingest endpoint. Goes through apiFetch so the
 *  bearer is attached in cloud mode — a plain fetch here 401s and the phone-as-GPS flow silently
 *  stalls on "waiting". */
export interface IngestFixBody {
  timestamp: number;
  lat: number;
  lon: number;
  accuracyM: number | null;
  altitude: number | null;
  speedMps: number | null;
  headingDeg: number | null;
}
export const ingestFix = (body: IngestFixBody) => post('/api/ingest/fix', body);

export const simDrive = (path: LonLat[], opts: { speedMps?: number; offsetM?: number } = {}) =>
  post('/api/sim/drive', { path, ...opts });
export const simNudge = (delta: number) => post('/api/sim/nudge', { delta });
export const simOffset = (m: number) => post('/api/sim/offset', { m });
export const simSpeed = (mps: number) => post('/api/sim/speed', { mps });
export const simStop = () => post('/api/sim/stop', {});

/** Connect to the live fix stream. Returns a disposer. Auto-reconnects on drop. When auth is on the
 *  access token rides as ?token= (browsers can't set WS headers); a 1008 close means the token was
 *  rejected/expired, so we refresh before reconnecting. */
export function connectFixStream(onFix: (fix: GnssFix) => void, onStatus?: (up: boolean) => void) {
  let ws: WebSocket | null = null;
  let closed = false;
  let retry: ReturnType<typeof setTimeout> | null = null;

  const open = () => {
    const proto = location.protocol === 'https:' ? 'wss' : 'ws';
    const t = token();
    const qs = t ? `?token=${encodeURIComponent(t)}` : '';
    ws = new WebSocket(`${proto}://${location.host}/ws/gnss${qs}`);
    ws.onopen = () => onStatus?.(true);
    ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data as string) as { type: string; fix: GnssFix };
        if (msg.type === 'fix') onFix(msg.fix);
      } catch {
        /* ignore malformed */
      }
    };
    ws.onclose = (ev) => {
      onStatus?.(false);
      if (closed) return;
      if (ev.code === 1008) {
        void updateToken().finally(() => {
          retry = setTimeout(open, 500);
        });
      } else {
        retry = setTimeout(open, 1000);
      }
    };
    ws.onerror = () => ws?.close();
  };
  open();

  return () => {
    closed = true;
    if (retry) clearTimeout(retry);
    ws?.close();
  };
}
